import React, { useEffect, useState, useRef } from 'react';
import { useAppContext } from '../../contexts/AppContext';
import { Truck } from '../../types';
import { collection, doc, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { db } from '../../config/firebase';

const MonitoringDataPage: React.FC = () => {
  const { state } = useAppContext();
  const currentUser = state.currentUser;
  const isAuthorized = currentUser?.email === 'argamtaha2009@gmail.com';
  const [reading, setReading] = useState<{ deviceId: string; temperature: number; vibration: number; healthScore: number; createdAt: Date | string | null; } | null>(null);
  const [recentReadings, setRecentReadings] = useState<Array<{ id: string; temperature: number; vibration: number; healthScore: number; createdAt: Date | string | null; alertStatus: string; }>>([]);
  const [last50Readings, setLast50Readings] = useState<Array<{ id: string; temperature: number; vibration: number; healthScore: number; createdAt: Date | string | null; alertStatus: string; }>>([]);
  const [linkedTruck, setLinkedTruck] = useState<string | null>(null);
  const [selectedTruckId, setSelectedTruckId] = useState<string>('');
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deviceStatus, setDeviceStatus] = useState<string>('Offline');
  const trucks = state.trucks || [];
  const latestReadingTimeRef = useRef<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const getAlertColor = (healthScore: number) => {
    if (healthScore >= 80) return 'green';
    if (healthScore >= 60) return 'orange';
    return 'red';
  };

  const getDeviceStatus = (timestampMs: number | null) => {
    if (!timestampMs) return 'Offline';
    const ageSeconds = (Date.now() - timestampMs) / 1000;
    if (ageSeconds < 20) return 'Online';
    if (ageSeconds <= 90) return 'Delayed';
    return 'Offline';
  };

  const getDeviceStatusColor = (value: string) => {
    if (value === 'Online') return 'green';
    if (value === 'Delayed') return 'orange';
    return 'red';
  };

  const handleLinkTruck = async () => {
    if (!reading || !selectedTruckId) return;
    const selectedTruck = trucks.find((truck: Truck) => truck.id === selectedTruckId);
    if (!selectedTruck) {
      setError('Selected truck not found.');
      return;
    }

    try {
      setError(null);
      setSaveStatus('Saving...');
      const deviceId = reading.deviceId || 'test-device-001';
      await setDoc(doc(db, 'deviceLinks', deviceId), {
        deviceId,
        truckId: selectedTruck.id,
        truckName: `${selectedTruck.make} ${selectedTruck.model} (${selectedTruck.licensePlate})`,
        updatedAt: serverTimestamp()
      }, { merge: true });
      setLinkedTruck(`${selectedTruck.make} ${selectedTruck.model} (${selectedTruck.licensePlate})`);
      setSaveStatus('Linked successfully');
    } catch (saveError: any) {
      console.error('Unable to save device link:', saveError);
      setError(saveError?.message || 'Unable to save device link.');
      setSaveStatus(null);
    }
  };

  const getAlertStatus = (healthScore: number) => {
    if (healthScore >= 80) return 'Normal';
    if (healthScore >= 60) return 'Warning';
    return 'Critical';
  };

  useEffect(() => {
    setError(null);
    setReading(null);
    setRecentReadings([]);
    setLast50Readings([]);
    setDeviceStatus('Offline');

    if (!isAuthorized) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const latestQuery = query(
      collection(db, 'esp32Readings'),
      orderBy('createdAt', 'desc'),
      limit(1)
    );

    const listQuery = query(
      collection(db, 'esp32Readings'),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const fiftyQuery = query(
      collection(db, 'esp32Readings'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribeLatest = onSnapshot(
      latestQuery,
      (snapshot) => {
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          const data = doc.data();
          const deviceId = data.deviceId || 'test-device-001';
          const createdAtDate = data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt ? new Date(data.createdAt) : null;
          const createdAtMs = createdAtDate ? createdAtDate.getTime() : null;
          const newReading = {
            deviceId,
            temperature: data.temperature || 0,
            vibration: data.vibration || 0,
            healthScore: data.healthScore || 0,
            createdAt: createdAtDate || null
          };
          setReading(newReading);
          latestReadingTimeRef.current = createdAtMs;
          setDeviceStatus(getDeviceStatus(createdAtMs));
          setSaveStatus(null);
          setSelectedTruckId('');
          setLinkedTruck(null);
          (async () => {
            try {
              const linkQuery = query(
                collection(db, 'deviceLinks'),
                where('deviceId', '==', deviceId),
                limit(1)
              );
              const linkSnapshot = await getDocs(linkQuery);
              if (!linkSnapshot.empty) {
                const linkDoc = linkSnapshot.docs[0].data();
                setLinkedTruck(linkDoc.truckName || 'Not assigned yet');
                setSelectedTruckId(linkDoc.truckId || '');
              } else {
                setLinkedTruck(null);
                setSelectedTruckId('');
              }
            } catch (linkError) {
              console.error('Unable to load device link:', linkError);
              setLinkedTruck(null);
            }
          })();
        } else {
          setReading(null);
          latestReadingTimeRef.current = null;
          setDeviceStatus('Offline');
          setLinkedTruck(null);
        }
        setLoading(false);
      },
      (err) => {
        setError(err?.message || 'Unable to load latest ESP32 reading.');
        setLoading(false);
      }
    );

    const unsubscribeList = onSnapshot(
      listQuery,
      (snapshot) => {
        const readings = snapshot.docs.map((doc) => {
          const data = doc.data();
          const healthScore = data.healthScore || 0;
          return {
            id: doc.id,
            temperature: data.temperature || 0,
            vibration: data.vibration || 0,
            healthScore,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt || null,
            alertStatus: getAlertStatus(healthScore)
          };
        });
        setRecentReadings(readings);
      },
      (err) => {
        setError(err?.message || 'Unable to load recent ESP32 readings.');
      }
    );

    const unsubscribeFifty = onSnapshot(
      fiftyQuery,
      (snapshot) => {
        const readings = snapshot.docs.map((doc) => {
          const data = doc.data();
          const healthScore = data.healthScore || 0;
          return {
            id: doc.id,
            temperature: data.temperature || 0,
            vibration: data.vibration || 0,
            healthScore,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt || null,
            alertStatus: getAlertStatus(healthScore)
          };
        });
        setLast50Readings(readings);
      },
      (err) => {
        setError(err?.message || 'Unable to load last 50 ESP32 readings.');
      }
    );

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      setDeviceStatus(getDeviceStatus(latestReadingTimeRef.current));
    }, 5000);

    return () => {
      clearInterval(intervalRef.current!);
      unsubscribeLatest();
      unsubscribeList();
      unsubscribeFifty();
    };
  }, [isAuthorized]);

  const formatDate = (value: Date | string | null) => {
    if (!value) return 'No timestamp';
    const date = value instanceof Date ? value : new Date(value);
    return date.toLocaleString();
  };

  // Helper to render simple line chart
  const renderLineChart = (data: number[], label: string, color: string) => {
    if (data.length === 0) return <p>No data for {label}</p>;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const width = 400;
    const height = 100;
    const points = data.map((val, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((val - min) / range) * height;
      return `${x},${y}`;
    }).join(' ');
    return (
      <div>
        <h4>{label}</h4>
        <svg width={width} height={height} style={{ border: '1px solid #ccc' }}>
          <polyline fill="none" stroke={color} strokeWidth="2" points={points} />
        </svg>
      </div>
    );
  };

  // Calculate summaries
  const avgHealth = last50Readings.length > 0 ? (last50Readings.reduce((sum, r) => sum + r.healthScore, 0) / last50Readings.length).toFixed(1) : 'N/A';
  const maxVibration = last50Readings.length > 0 ? Math.max(...last50Readings.map(r => r.vibration)) : 'N/A';
  const maxTemperature = last50Readings.length > 0 ? Math.max(...last50Readings.map(r => r.temperature)) : 'N/A';
  const warningCriticalCount = last50Readings.filter(r => r.alertStatus === 'Warning' || r.alertStatus === 'Critical').length;
  const currentAlertStatus = reading ? getAlertStatus(reading.healthScore) : null;

  if (!isAuthorized) {
    return (
      <div className="card">
        <div className="card-header">
          <h1 className="card-title">Monitoring Data</h1>
        </div>
        <div className="card-body">
          <p>Work in progress. Check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card mb-6">
        <div className="card-header">
          <h1 className="card-title">Monitoring Data</h1>
        </div>
        <div className="card-body">
          {loading && <p>Loading latest reading...</p>}
          {error && <p className="text-red-600">{error}</p>}
          {!loading && !error && !reading && <p>No readings found.</p>}
          {reading && (
            <>
              {currentAlertStatus && currentAlertStatus !== 'Normal' && (
                <div className={`alert-badge ${currentAlertStatus.toLowerCase()}`} style={{ marginBottom: '1rem' }}>
                  <strong>{currentAlertStatus}:</strong> Current device health is {reading.healthScore}%.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <div className="p-4 border rounded">
                  <h3 className="font-semibold mb-2">Device ID</h3>
                  <p>{reading.deviceId}</p>
                </div>
                <div className="p-4 border rounded">
                  <h3 className="font-semibold mb-2">Device Status</h3>
                  <span style={{ color: getDeviceStatusColor(deviceStatus), fontWeight: 'bold' }}>
                    {deviceStatus}
                  </span>
                </div>
                <div className="p-4 border rounded">
                  <h3 className="font-semibold mb-2">Linked Truck</h3>
                  <p>{linkedTruck || 'Not assigned yet'}</p>
                </div>
                <div className="p-4 border rounded">
                  <h3 className="font-semibold mb-2">Temperature</h3>
                  <p>{reading.temperature}°C</p>
                </div>
                <div className="p-4 border rounded">
                  <h3 className="font-semibold mb-2">Vibration</h3>
                  <p>{reading.vibration} mm/s</p>
                </div>
                <div className="p-4 border rounded">
                  <h3 className="font-semibold mb-2">Health Score</h3>
                  <p>{reading.healthScore}% {' '}
                    <span style={{ fontWeight: 'bold', color: getAlertColor(reading.healthScore) }}>
                      ({getAlertStatus(reading.healthScore)})
                    </span>
                  </p>
                </div>
              </div>
              <div className="mb-4">
                <h3 className="font-semibold mb-2">Link Device to Truck</h3>
                <div className="mt-2">
                  {trucks.length > 0 ? (
                    <select
                      value={selectedTruckId}
                      onChange={(e) => setSelectedTruckId(e.target.value)}
                      style={{ width: '100%', padding: '0.5rem' }}
                    >
                      <option value="">Choose a truck...</option>
                      {trucks.map((truck: Truck) => (
                        <option key={truck.id} value={truck.id}>
                          {truck.make} {truck.model} ({truck.licensePlate})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p>No trucks available to link.</p>
                  )}
                </div>
                {trucks.length > 0 && (
                  <button
                    onClick={handleLinkTruck}
                    disabled={!selectedTruckId}
                    style={{ marginTop: '0.5rem', padding: '0.6rem 1rem' }}
                  >
                    Link Device to Truck
                  </button>
                )}
                {saveStatus && <p>{saveStatus}</p>}
              </div>
              <div>
                <strong>Last updated:</strong> {formatDate(reading.createdAt)}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-header">
          <h2 className="card-title">Summary Cards (Last 50 Readings)</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 border rounded">
              <strong>Average Health Score:</strong> {avgHealth}%
            </div>
            <div className="p-4 border rounded">
              <strong>Highest Vibration:</strong> {maxVibration} mm/s
            </div>
            <div className="p-4 border rounded">
              <strong>Highest Temperature:</strong> {maxTemperature}°C
            </div>
            <div className="p-4 border rounded">
              <strong>Warning/Critical Count:</strong> {warningCriticalCount}
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-header">
          <h2 className="card-title">Trend Graphs (Last 50 Readings)</h2>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            {renderLineChart(last50Readings.map(r => r.healthScore).reverse(), 'Health Score Trend', 'blue')}
            {renderLineChart(last50Readings.map(r => r.temperature).reverse(), 'Temperature Trend', 'red')}
            {renderLineChart(last50Readings.map(r => r.vibration).reverse(), 'Vibration Trend', 'green')}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Last 10 Readings</h2>
        </div>
        <div className="card-body">
          {recentReadings.length === 0 && <p>No recent readings available.</p>}
          {recentReadings.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Temp</th>
                    <th>Vibration</th>
                    <th>Health</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentReadings.map((item) => (
                    <tr key={item.id}>
                      <td>{formatDate(item.createdAt)}</td>
                      <td>{item.temperature}°C</td>
                      <td>{item.vibration} mm/s</td>
                      <td>{item.healthScore}%</td>
                      <td>{item.alertStatus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MonitoringDataPage;
